<script lang="ts" setup>
const { getContinentAttributes, getCountryAttributes, getMetaAttributes, getStateAttributes } = useApiAttributes()
const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:locations-search-counties',
  () => api.locations.counties.fetch(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/locations/counties',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.page'),
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.search'),
    name: 'search',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.size'),
    name: 'size',
    optional: true,
    type: 'number',
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.counties'),
    name: 'counties',
    type: 'array',

    attributes: [
      {
        description: t('response.code'),
        name: 'code',
        parent: 'county',
        type: 'string',
      },
      {
        attributes: getContinentAttributes({ parent: 'county' }),
        description: t('response.continent'),
        name: 'continent',
        parent: 'county',
        type: 'object',
      },
      {
        attributes: getCountryAttributes({ parent: 'county' }),
        description: t('response.country'),
        name: 'country',
        parent: 'county',
        type: 'object',
      },
      {
        description: t('response.latitude'),
        name: 'latitude',
        parent: 'county',
        type: 'string',
      },
      {
        description: t('response.longitude'),
        name: 'longitude',
        parent: 'county',
        type: 'string',
      },
      {
        description: t('response.name'),
        name: 'name',
        parent: 'county',
        type: 'string',
      },
      {
        attributes: getStateAttributes({ parent: 'county' }),
        description: t('response.state'),
        name: 'state',
        parent: 'county',
        type: 'string',
      },
    ],
  },
  getMetaAttributes(),
]

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    await execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "page": "The page to fetch (default to 1).",
      "search": "An additional search query applied to the county name.",
      "size": "The number of counties to be returned (between 1 and 20). Default to 10."
    },
    "response": {
      "code": "The county code.",
      "continent": "The county's continent.",
      "counties": "The counties that match your conditions.",
      "country": "The county's country.",
      "latitude": "The county's latitude.",
      "longitude": "The county's longitude.",
      "name": "The county's name.",
      "state": "The county's state."
    }
  },
  "es": {
    "attributes": {
      "page": "La página a recuperar (por defecto 1).",
      "search": "Una consulta de búsqueda adicional aplicada al nombre del departamento.",
      "size": "El número de departamentos a devolver (entre 1 y 20). Por defecto 10."
    },
    "response": {
      "code": "El código del departamento.",
      "continent": "El continente del departamento.",
      "counties": "Los departamentos que coinciden con tus criterios.",
      "country": "El país del departamento.",
      "latitude": "La latitud del departamento.",
      "longitude": "La longitud del departamento.",
      "name": "El nombre del departamento.",
      "state": "El estado del departamento."
    }
  },
  "fr": {
    "attributes": {
      "page": "La page à récupérer (par défaut 1).",
      "search": "Une requête de recherche supplémentaire appliquée au nom du département.",
      "size": "Le nombre de départements à retourner (entre 1 et 20). Par défaut 10."
    },
    "response": {
      "code": "Le code du département.",
      "continent": "Le continent du département.",
      "counties": "Les départements qui correspondent à vos critères.",
      "country": "Le pays du département.",
      "latitude": "La latitude du département.",
      "longitude": "La longitude du département.",
      "name": "Le nom du département.",
      "state": "L'état du département."
    }
  }
}
</i18n>
